const STORAGE_KEYS = {
    EMAIL: 'signup_email',
    EMAIL_VERIFIED: 'signup_email_verified',
    IS_RETURNING_USER: 'signup_is_returning_user',
    APPLICATION_ID: 'signup_application_id',
    REFERRAL_ID: 'signup_referral_id',
    TWA_MIGRATE_TOKEN: 'signup_twa_migrate_token',
    UTM_PARAMS: 'signup_utm_params',
    // ADR-019 §4 per-user referral captured from /r/dispatcher landing (shared key with ReferralLandingPage).
    // localStorage so it survives the Google/Outlook full-page OAuth redirect (like UTM_PARAMS).
    USER_REFERRAL: 'numeo_referral',
} as const

/** ADR-019 §4 — a per-user referral captured at web signup, attributed once the referee's email is known. */
export interface PendingUserReferral {
    code: string
    type: 'driver' | 'dispatcher'
}

export interface UtmParams {
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_term?: string
    utm_content?: string
}

export const getSignupEmail = (): string | null => {
    return sessionStorage.getItem(STORAGE_KEYS.EMAIL)
}

export const setSignupEmail = (email: string): void => {
    sessionStorage.setItem(STORAGE_KEYS.EMAIL, email)
}

/**
 * Client-side UX guard only — prevents users from skipping ahead in the signup flow.
 * Actual email verification is enforced server-side in `extensionCompleteSignUp`
 * via `ExtensionSignUpTempRepo().findOne({ email, isVerified: true })`.
 */
export const isEmailVerified = (): boolean => {
    return sessionStorage.getItem(STORAGE_KEYS.EMAIL_VERIFIED) === 'true'
}

export const setEmailVerified = (status: boolean): void => {
    sessionStorage.setItem(STORAGE_KEYS.EMAIL_VERIFIED, status ? 'true' : 'false')
}

export const setIsReturningUser = (status: boolean): void => {
    sessionStorage.setItem(STORAGE_KEYS.IS_RETURNING_USER, status ? 'true' : 'false')
}

export const getApplicationId = (): string | null => {
    return sessionStorage.getItem(STORAGE_KEYS.APPLICATION_ID)
}

export const setApplicationId = (id: string): void => {
    sessionStorage.setItem(STORAGE_KEYS.APPLICATION_ID, id)
}

export const getReferralId = (): string | null => {
    return sessionStorage.getItem(STORAGE_KEYS.REFERRAL_ID)
}

export const setReferralId = (id: string): void => {
    sessionStorage.setItem(STORAGE_KEYS.REFERRAL_ID, id)
}

/**
 * TWA (Telegram Web App) guest migration token.
 *
 * Deliberately stored in `localStorage` (not `sessionStorage` like the other
 * signup keys) because the Google/Outlook OAuth flow does a full-page redirect,
 * which would wipe sessionStorage. localStorage survives that round-trip so a
 * later step can read the token to migrate the Telegram mini-app guest.
 */
/**
 * Short TTL on the stored migrate token. Long enough to survive the Google/Outlook full-page redirect
 * round-trip, short enough that an ABANDONED token can't be silently replayed by a DIFFERENT user who
 * logs in later in the same (e.g. shared) browser — bounding the cross-account migration window.
 */
const TWA_MIGRATE_TOKEN_TTL_MS = 60 * 60 * 1000

export const getTwaMigrateToken = (): string | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.TWA_MIGRATE_TOKEN)
    if (!raw) return null
    try {
        const { token, ts } = JSON.parse(raw) as { token?: string; ts?: number }
        if (!token || !ts || Date.now() - ts > TWA_MIGRATE_TOKEN_TTL_MS) {
            localStorage.removeItem(STORAGE_KEYS.TWA_MIGRATE_TOKEN)
            return null
        }
        return token
    } catch {
        // Legacy/raw value or corrupt — drop it (can't verify freshness).
        localStorage.removeItem(STORAGE_KEYS.TWA_MIGRATE_TOKEN)
        return null
    }
}

export const setTwaMigrateToken = (token: string): void => {
    localStorage.setItem(STORAGE_KEYS.TWA_MIGRATE_TOKEN, JSON.stringify({ token, ts: Date.now() }))
}

export const clearTwaMigrateToken = (): void => {
    localStorage.removeItem(STORAGE_KEYS.TWA_MIGRATE_TOKEN)
}

/**
 * UTM attribution params captured on landing. Stored in `localStorage` for the
 * same reason as the TWA token — they must survive the full-page OAuth redirect
 * so a later step can attribute the signup. Undefined values are omitted.
 */
export const getUtmParams = (): UtmParams | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.UTM_PARAMS)
    if (!raw) return null
    try {
        return JSON.parse(raw) as UtmParams
    } catch {
        return null
    }
}

export const setUtmParams = (params: UtmParams): void => {
    localStorage.setItem(STORAGE_KEYS.UTM_PARAMS, JSON.stringify(params))
}

export const clearUtmParams = (): void => {
    localStorage.removeItem(STORAGE_KEYS.UTM_PARAMS)
}

/**
 * ADR-019 §4 — the per-user referral the referee arrived with (set by the /r/dispatcher landing, or
 * captured from `?uref=&reftype=` on the onboarding landing). Read at signup completion to attribute
 * the referral, then cleared. Deliberately NOT cleared by `clearSignupData` — attribution clears it itself.
 */
export const getUserReferral = (): PendingUserReferral | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.USER_REFERRAL)
    if (!raw) return null
    try {
        const v = JSON.parse(raw) as Partial<PendingUserReferral>
        return v?.code && (v.type === 'driver' || v.type === 'dispatcher') ? { code: v.code, type: v.type } : null
    } catch {
        return null
    }
}

export const setUserReferral = (referral: PendingUserReferral): void => {
    localStorage.setItem(STORAGE_KEYS.USER_REFERRAL, JSON.stringify(referral))
}

export const clearUserReferral = (): void => {
    localStorage.removeItem(STORAGE_KEYS.USER_REFERRAL)
}

export const clearSignupData = (): void => {
    sessionStorage.removeItem(STORAGE_KEYS.EMAIL)
    sessionStorage.removeItem(STORAGE_KEYS.EMAIL_VERIFIED)
    sessionStorage.removeItem(STORAGE_KEYS.IS_RETURNING_USER)
    sessionStorage.removeItem(STORAGE_KEYS.APPLICATION_ID)
    sessionStorage.removeItem(STORAGE_KEYS.REFERRAL_ID)
    // Drop the TWA migrate token too so an abandoned token isn't replayed by a later/other session.
    clearTwaMigrateToken()
}
